/**
 * A version's build: how it is going, and what to do when it is blocked.
 *
 * Starting a build is the easy part; the reason this exists is the two outcomes people
 * get stuck on — a failure whose message is in CloudWatch, and an image the scanner
 * blocked. Both need somewhere to land.
 */

import { useEffect, useState } from "react";

import { ApiError } from "@/api/client";

/** A build that has not reached a terminal state, so it is still worth polling. */
function isInFlight(status: Build["status"]): boolean {
  return status === "building" || status === "pending";
}

import type { Build } from "@/api/endpoints";
import { endpoints } from "@/api/endpoints";
import { useProject } from "@/components/ProjectLayout";
import { Badge, Field, Loading, Notice, RelativeTime, formatDuration } from "@/components/ui";
import { useAsync } from "@/hooks/useAsync";

function StatusBadge({ build }: { build: Build }) {
  switch (build.status) {
    case "succeeded":
      return <Badge tone="success">Succeeded</Badge>;
    case "failed":
      return <Badge tone="danger">Failed</Badge>;
    case "blocked":
      return <Badge tone="danger">Blocked by scan</Badge>;
    case "building":
      return (
        <Badge tone="accent" live>
          Building
        </Badge>
      );
    default:
      return <Badge>Pending</Badge>;
  }
}

function severityCounts(build: Build): Array<[string, number]> {
  const findings = build.scan_findings as
    | { severity_counts?: Record<string, number>; findingSeverityCounts?: Record<string, number> }
    | null;
  const counts = findings?.severity_counts ?? findings?.findingSeverityCounts ?? {};
  return Object.entries(counts).filter(([, count]) => count > 0);
}

function OverrideScan({
  scriptRef,
  build,
  onOverridden,
}: {
  scriptRef: string;
  build: Build;
  onOverridden: () => void;
}) {
  const { project, can } = useProject();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string>();

  if (!can("admin")) {
    return (
      <Notice tone="warning">
        A project administrator can release this deliberately, and that decision is
        recorded against their name.
      </Notice>
    );
  }

  if (!open) {
    return (
      <button type="button" className="button button--small button--danger" onClick={() => setOpen(true)}>
        Release this image anyway
      </button>
    );
  }

  const submit = async () => {
    setBusy(true);
    setProblem(undefined);
    try {
      await endpoints.overrideScan(project.slug, scriptRef, build.id, reason);
      setOpen(false);
      onOverridden();
    } catch (caught) {
      setProblem(caught instanceof ApiError ? caught.message : (caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack" style={{ gap: 8 }}>
      {problem && <Notice tone="danger">{problem}</Notice>}
      <Field
        label="Why is this safe to release?"
        hint="At least 10 characters. Recorded against your name, permanently."
        htmlFor={`override-${build.id}`}
      >
        <input
          id={`override-${build.id}`}
          className="input"
          value={reason}
          autoFocus
          onChange={(event) => setReason(event.target.value)}
        />
      </Field>
      <div className="row">
        <button
          type="button"
          className="button button--small button--danger"
          disabled={busy || reason.trim().length < 10}
          onClick={submit}
        >
          Release anyway
        </button>
        <button
          type="button"
          className="button button--small button--ghost"
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export function BuildPanel({
  scriptRef,
  versionId,
  onChanged,
}: {
  scriptRef: string;
  versionId: string;
  onChanged: () => void;
}) {
  const { project } = useProject();

  const { data, loading, reload } = useAsync(
    () => endpoints.builds(project.slug, scriptRef, versionId),
    [project.slug, scriptRef, versionId],
  );

  const listed = data ?? [];
  const listedLatest = listed[0];
  const listedInFlight =
    listedLatest?.status === "building" || listedLatest?.status === "pending";

  // Reading a build refreshes it from CodeBuild, so polling is what advances it. The
  // polled copy is what gets rendered: the list around it is fetched once, so trusting
  // that would leave the badge saying "building" long after the image was pushed.
  const { data: polled } = useAsync(
    async () =>
      listedLatest && listedInFlight
        ? await endpoints.build(project.slug, scriptRef, listedLatest.id)
        : null,
    [project.slug, scriptRef, listedLatest?.id, listedInFlight],
    { pollMs: listedInFlight ? 5_000 : undefined, enabled: Boolean(listedLatest && listedInFlight) },
  );

  const latest =
    polled && listedLatest && polled.id === listedLatest.id ? polled : listedLatest;
  const builds = latest && listed.length ? [latest, ...listed.slice(1)] : listed;
  const inFlight = latest?.status === "building" || latest?.status === "pending";

  // A build that just finished changes more than this panel: the version becomes
  // runnable, or it does not and the run button has to stay disabled.
  const settled = Boolean(polled && !isInFlight(polled.status));
  useEffect(() => {
    if (settled) {
      reload();
      onChanged();
    }
    // Only when it settles — reload/onChanged identities would otherwise re-fire this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled]);

  if (loading) return <Loading rows={2} label="Loading builds" />;
  if (builds.length === 0) return null;

  const counts = latest ? severityCounts(latest) : [];

  return (
    <div className="stack" style={{ gap: 10 }}>
      {latest && (
        <div className="row" style={{ gap: 10 }}>
          <StatusBadge build={latest} />
          <span className="muted" style={{ fontSize: 12.5 }}>
            {latest.finished_at ? (
              <>
                finished <RelativeTime iso={latest.finished_at} />
                {latest.started_at && (
                  <>
                    {" "}
                    in{" "}
                    {formatDuration(
                      new Date(latest.finished_at).getTime() -
                        new Date(latest.started_at).getTime(),
                    )}
                  </>
                )}
              </>
            ) : latest.started_at ? (
              <>
                started <RelativeTime iso={latest.started_at} />
              </>
            ) : (
              "queued"
            )}
          </span>
          {inFlight && (
            <button type="button" className="button button--small button--ghost" onClick={reload}>
              Refresh
            </button>
          )}
        </div>
      )}

      {latest?.error_message && (
        <Notice tone={latest.status === "blocked" ? "warning" : "danger"}>
          {latest.error_message}
        </Notice>
      )}

      {counts.length > 0 && (
        <div className="row" style={{ gap: 8 }}>
          <span className="muted" style={{ fontSize: 12.5 }}>
            Image scan:
          </span>
          {counts.map(([severity, count]) => (
            <Badge
              key={severity}
              tone={severity.toUpperCase() === "CRITICAL" ? "danger" : "warning"}
            >
              {count} {severity.toLowerCase()}
            </Badge>
          ))}
        </div>
      )}

      {latest?.status === "blocked" && (
        <OverrideScan
          scriptRef={scriptRef}
          build={latest}
          onOverridden={() => {
            reload();
            onChanged();
          }}
        />
      )}

      {latest?.log_group && latest.log_stream && (
        <div className="faint mono" style={{ fontSize: 11.5 }}>
          Logs: {latest.log_group} / {latest.log_stream}
        </div>
      )}

      {builds.length > 1 && (
        <details>
          <summary className="muted" style={{ cursor: "pointer", fontSize: 12.5 }}>
            {builds.length - 1} earlier {builds.length === 2 ? "attempt" : "attempts"}
          </summary>
          <ul className="muted" style={{ fontSize: 12.5, paddingLeft: 18, margin: "6px 0 0" }}>
            {builds.slice(1).map((build) => (
              <li key={build.id}>
                {build.status} — <RelativeTime iso={build.created_at} />
                {build.error_message && <span className="faint"> · {build.error_message}</span>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
